import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

/**
 * Intensity hourly trend chart (§7.3)
 *
 * Props:
 *   type:     'energy' | 'cost'
 *   data:     [{ timestamp, energy_intensity, cost_intensity, status, ... }]
 *   currency: string (used in cost-mode label)
 *   period:   '6h' | '24h' | '7d' | '30d' | ... — drives the X-axis format.
 *             Passed by the hub so we don't infer span from the data window
 *             (which can mislead when the data subset is shorter than the
 *             selected period — e.g. compute only ran for the last 24h).
 *
 * X-axis: compact ticks. For ≤24h periods we render "HH:00"; for multi-day
 * periods we render "Mon dd". The full date+time stays in the tooltip.
 * Tick density is bounded so labels never overlap regardless of point count.
 */

const pad2 = (n) => String(n).padStart(2, '0');

// Period strings that fit in a single calendar day. Anything else is treated
// as multi-day and gets date labels.
const INTRADAY_PERIODS = new Set(['1h', '3h', '6h', '12h', '24h']);

const formatTooltipDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const IntensityChart = ({ type = 'energy', data = [], currency = 'INR', period = '24h' }) => {
  const dataKey = type === 'cost' ? 'cost_intensity' : 'energy_intensity';
  const label = type === 'cost' ? `Cost Intensity (${currency}/kL)` : 'Energy Intensity (kWh/kL)';
  const stroke = type === 'cost' ? '#0d9488' : '#2563eb'; // teal vs blue

  // Use the SELECTED period — not the actual data span — to choose label
  // format. This keeps the X-axis honest when the data subset is shorter than
  // the chosen window (e.g. compute only ran for 24h but user picked 7d).
  const isMultiDay = useMemo(() => {
    if (!INTRADAY_PERIODS.has(period)) return true;
    return false;
  }, [period]);

  const tickFormatter = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    if (isMultiDay) {
      // Compact day label, e.g. "Apr 29"
      return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
    }
    // Intra-day, just hour. e.g. "15:00"
    return `${pad2(d.getHours())}:00`;
  };

  if ((data || []).length === 0) {
    return (
      <div className="h-72 flex items-center justify-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <div className="text-center">
          <div className="text-sm font-medium">No data for this period</div>
          <div className="text-xs mt-1">Upload CSVs and run /api/oim/compute to populate</div>
        </div>
      </div>
    );
  }

  // Cap tick count to ~10 to keep labels readable. minTickGap is the safety
  // net even if interval would otherwise let labels overlap.
  const interval = data.length <= 12 ? 0 : Math.max(0, Math.floor(data.length / 10));

  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 20, left: 5, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="timestamp"
            tickFormatter={tickFormatter}
            tick={{ fontSize: 11, fill: '#4b5563' }}
            interval={interval}
            minTickGap={28}
            tickMargin={8}
            height={32}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#4b5563' }}
            width={48}
            allowDecimals
          />
          <Tooltip
            formatter={(value) => {
              if (value == null) return ['—', label];
              const formatted = typeof value === 'number' ? value.toFixed(4) : value;
              return [formatted, label];
            }}
            labelFormatter={(iso, payload) => {
              const status = payload && payload[0] && payload[0].payload && payload[0].payload.status;
              const dt = formatTooltipDate(iso);
              return status ? `${dt} · ${status}` : dt;
            }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={stroke}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 5 }}
            connectNulls={false}
            name={label}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default IntensityChart;
